/**
 * UK phone-number helpers for MFA (SMS) enrollment.
 * -------------------------------------------------
 * Extracted from PatientSetUpMFA so the provider and service-user MFA setup
 * pages (and the shared `useMfaEnrollment` hook) all format and validate phone
 * numbers the same way. Pure functions with no side effects — trivially unit
 * testable and safe to reuse anywhere.
 *
 * Business context: we only support UK mobile numbers for SMS-based 2FA. Firebase
 * expects numbers in E.164 format (e.g. "+447911123456"), but users type them in
 * lots of shapes ("07911 123456", "7911123456", "+44 7911 123456"). These helpers
 * normalise whatever the user types into E.164 and validate that the result is a
 * plausible UK mobile.
 */

/**
 * Normalise a user-entered UK phone number into E.164 format ("+44…").
 *
 * Handles the common shapes people type:
 *   - Already international ("+447911123456")      -> returned as-is (spaces stripped)
 *   - UK domestic with leading 0 ("07911123456")  -> "+447911123456"
 *   - Country code without + ("447911123456")      -> "+447911123456"
 *   - Bare mobile without 0 or CC ("7911123456")   -> "+447911123456"
 *   - Anything else                                -> "+" prefixed as a last resort
 *
 * @param {string} phoneNumber Raw user input (may contain spaces, dashes, brackets).
 * @returns {string} The number in E.164-ish format.
 */
export function formatUKPhoneNumber(phoneNumber) {
  // Remove any spaces, dashes, parentheses, etc. so the checks below are simple.
  const cleaned = (phoneNumber || '').replace(/\s+|\(|\)|-/g, '');

  // If already in international format with +, return as is.
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // If starts with 0 (UK domestic format), replace the leading 0 with +44.
  if (cleaned.startsWith('0')) {
    return '+44' + cleaned.substring(1);
  }

  // If starts with 44 (country code without +), just add the +.
  if (cleaned.startsWith('44')) {
    return '+' + cleaned;
  }

  // If it's just the subscriber number without country code or leading 0
  // (e.g. "7911123456"), assume UK and prefix +44.
  if (/^7\d{9,10}$/.test(cleaned)) {
    return '+44' + cleaned;
  }

  // Default case - just add + if not present so it's at least E.164-shaped.
  return '+' + cleaned;
}

/**
 * Validate that user input resolves to a UK mobile number.
 *
 * We normalise first, then require exactly "+447" followed by 9 digits — the
 * canonical shape of a UK mobile in E.164. This keeps the "valid ✔" indicator
 * and the Send-code button gate honest.
 *
 * @param {string} input Raw user input.
 * @returns {boolean} True if the number is a valid UK mobile.
 */
export function isValidUKPhone(input) {
  const formatted = formatUKPhoneNumber(input);
  return /^\+447\d{9}$/.test(formatted);
}
